import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Shield, Building2, MapPin, Cpu, Users, UserCheck, Phone,
  Loader2, CheckCircle2, Plus, Trash2, ChevronRight, ChevronLeft, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'school', label: 'Skolan', icon: Building2 },
  { id: 'departments', label: 'Avdelningar', icon: MapPin },
  { id: 'sensors', label: 'Sensorer', icon: Cpu },
  { id: 'users', label: 'Användare', icon: Users },
  { id: 'contact', label: 'Kontakt', icon: Phone },
  { id: 'review', label: 'Granska', icon: CheckCircle2 },
];

export default function CustomerOnboardingPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [maxSensors, setMaxSensors] = useState(14);

  const [form, setForm] = useState({
    school_name: '', school_address: '',
    departments: [{ name: '' }],
    sensors: [],
    users: [{ name: '', email: '', role: '', receives_alarms: true, alarm_departments: [] }],
    primary_contact: { name: '', email: '', phone: '' },
    notes: '', installation_info: '',
  });

  useEffect(() => {
    loadForm();
  }, [token]);

  const loadForm = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/form/${token}`);
      const d = res.data;
      setMaxSensors(d.max_sensors || 14);
      if (d.status === 'submitted') {
        setSubmitted(true);
      }
      setForm(prev => ({
        ...prev,
        school_name: d.school_name || '',
        school_address: d.school_address || '',
        departments: d.departments?.length ? d.departments : [{ name: '' }],
        sensors: d.sensors?.length ? d.sensors : Array.from({ length: d.max_sensors || 14 }, (_, i) => ({ name: '', department: '', type: 'active' })),
        users: d.users?.length ? d.users : [{ name: '', email: '', role: '', receives_alarms: true, alarm_departments: [] }],
        primary_contact: d.primary_contact?.name ? d.primary_contact : { name: '', email: '', phone: '' },
        notes: d.notes || '',
        installation_info: d.installation_info || '',
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte ladda formuläret');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Compose full sensor names: "Department – Room name"
      const composedForm = {
        ...form,
        sensors: form.sensors.map(s => ({
          ...s,
          name: s.department && s.name ? `${s.department} – ${s.name}` : s.name,
        })),
      };
      await axios.post(`${API_URL}/api/onboarding/form/${token}/submit`, composedForm);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte skicka in');
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateSensor = (i, field, value) => {
    setForm(prev => {
      const sensors = [...prev.sensors];
      sensors[i] = { ...sensors[i], [field]: value };
      return { ...prev, sensors };
    });
  };
  const updateUser = (i, field, value) => {
    setForm(prev => {
      const users = [...prev.users];
      users[i] = { ...users[i], [field]: value };
      return { ...prev, users };
    });
  };
  const addUser = () => setForm(prev => ({ ...prev, users: [...prev.users, { name: '', email: '', role: '', receives_alarms: true, alarm_departments: [] }] }));
  const removeUser = (i) => setForm(prev => ({ ...prev, users: prev.users.filter((_, j) => j !== i) }));
  const updateDept = (i, value) => {
    setForm(prev => {
      const departments = [...prev.departments];
      departments[i] = { name: value };
      return { ...prev, departments };
    });
  };
  const addDept = () => setForm(prev => ({ ...prev, departments: [...prev.departments, { name: '' }] }));
  const removeDept = (i) => setForm(prev => ({ ...prev, departments: prev.departments.filter((_, j) => j !== i) }));
  const updateContact = (field, value) => setForm(prev => ({ ...prev, primary_contact: { ...prev.primary_contact, [field]: value } }));

  const deptNames = form.departments.filter(d => d.name).map(d => d.name);

  // No auto-distribution — user adds sensors per department manually
  const distributeSensors = () => {
    if (deptNames.length === 0) return;
    const currentSensors = form.sensors;
    // If sensors already have departments, keep them
    const assigned = currentSensors.filter(s => s.department && deptNames.includes(s.department));
    if (assigned.length > 0) return;
    // Start with 1 empty sensor per department so it's not blank
    const newSensors = deptNames.map(dept => ({ name: '', department: dept, type: 'active' }));
    setForm(prev => ({ ...prev, sensors: newSensors }));
  };

  const addSensorToDept = (dept) => {
    const total = form.sensors.length;
    if (total >= maxSensors) return;
    setForm(prev => ({ ...prev, sensors: [...prev.sensors, { name: '', department: dept, type: 'active' }] }));
  };

  const removeSensor = (idx) => {
    setForm(prev => ({ ...prev, sensors: prev.sensors.filter((_, i) => i !== idx) }));
  };

  const totalSensors = form.sensors.length;
  const filledSensors = form.sensors.filter(s => s.name).length;

  // Auto-distribute sensors when entering step 3
  useEffect(() => {
    if (step === 2) distributeSensors();
  }, [step]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !form.school_name && !submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
          <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 font-ibm">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full"><CardContent className="p-8 text-center">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="font-chivo font-black text-2xl text-navy mb-2">Tack!</h1>
          <p className="text-slate-600 font-ibm">
            Era uppgifter har skickats in. Vi konfigurerar ert system och återkommer med inloggningsuppgifter.
          </p>
        </CardContent></Card>
      </div>
    );
  }

  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-onboarding-page">
      {/* Header */}
      <div className="bg-navy text-white py-5 px-4 sm:py-6 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-5 w-5" />
            <span className="font-chivo font-black text-lg">Vapelarm Cloud</span>
          </div>
          <h1 className="font-chivo font-black text-xl sm:text-2xl mt-2">Kundregistrering</h1>
          <p className="text-slate-300 font-ibm text-sm mt-1">
            Fyll i uppgifterna nedan så konfigurerar vi ert system inför driftsättning.
          </p>
        </div>
      </div>

      {/* Step indicator — compact on mobile */}
      <div className="bg-white border-b border-slate-200 py-2.5 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto flex gap-1 sm:gap-1.5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isActive = i === step;
            const isDone = i < step;
            return (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                className={`flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 sm:py-1.5 rounded-md text-xs font-ibm font-medium transition-colors flex-1 sm:flex-none min-h-[36px] ${
                  isActive ? 'bg-navy text-white' : isDone ? 'bg-emerald-50 text-emerald-700' : 'text-slate-400 hover:bg-slate-50'
                }`}
                data-testid={`step-${s.id}`}
              >
                {isDone ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden text-[10px]">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <Card className="border-slate-200">
          <CardContent className="p-4 sm:p-6 md:p-8">

            {/* Step 1: School */}
            {step === 0 && (
              <div className="space-y-5">
                <h2 className="font-chivo font-bold text-lg text-navy">1. Skolans uppgifter</h2>
                <div>
                  <Label className="font-ibm text-xs mb-1.5 block">Skolans namn</Label>
                  <Input value={form.school_name} onChange={e => updateField('school_name', e.target.value)} placeholder="T.ex. Frykenskolan" className="h-11 sm:h-10 text-base sm:text-sm" data-testid="school-name" />
                </div>
                <div>
                  <Label className="font-ibm text-xs mb-1.5 block">Adress</Label>
                  <Input value={form.school_address} onChange={e => updateField('school_address', e.target.value)} placeholder="T.ex. Storgatan 1, 685 30 Torsby" className="h-11 sm:h-10 text-base sm:text-sm" data-testid="school-address" />
                </div>
              </div>
            )}

            {/* Step 2: Departments */}
            {step === 1 && (
              <div className="space-y-5">
                <h2 className="font-chivo font-bold text-lg text-navy">2. Avdelningar / byggnader</h2>
                <p className="text-sm text-slate-500 font-ibm">Ange hur skolan vill att de olika avdelningarna/områdena ska benämnas i systemet.</p>
                {form.departments.map((dept, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-ibm text-slate-400 w-6 shrink-0">{i + 1}.</span>
                    <Input value={dept.name} onChange={e => updateDept(i, e.target.value)} placeholder={`T.ex. Hus ${String.fromCharCode(65 + i)}`} className="flex-1 h-11 sm:h-10 text-base sm:text-sm" data-testid={`dept-${i}`} />
                    {form.departments.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-slate-400 shrink-0" onClick={() => removeDept(i)}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addDept} className="gap-1.5 h-10 sm:h-9 px-4" data-testid="add-dept-btn">
                  <Plus className="h-3.5 w-3.5" /> Lägg till avdelning
                </Button>
              </div>
            )}

            {/* Step 3: Sensors — grouped by department */}
            {step === 2 && (
              <div className="space-y-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="font-chivo font-bold text-lg text-navy">3. Namn på sensorplatser</h2>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-ibm px-2.5 py-1 rounded-full ${totalSensors >= maxSensors ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {totalSensors} av {maxSensors} platser
                    </span>
                    {totalSensors < maxSensors && (
                      <span className="text-xs font-ibm text-amber-600">{maxSensors - totalSensors} kvar</span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-slate-500 font-ibm">
                  Ange ett tydligt rumsnamn för varje sensor. Välj om det ska vara en aktiv sensor eller tomt skal.
                  {deptNames.length > 0 && <> Avdelningsnamnet läggs till automatiskt, t.ex. <strong>Toalett 101</strong> blir <strong>{deptNames[0]} – Toalett 101</strong>.</>}
                </p>

                {deptNames.length > 0 ? (
                  // Grouped by department
                  <>
                    {deptNames.map(dept => {
                      const deptSensors = form.sensors
                        .map((s, i) => ({ ...s, _idx: i }))
                        .filter(s => s.department === dept);
                      return (
                        <div key={dept} className="border border-slate-200 rounded-lg p-3 sm:p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-blue-500" />
                              <h3 className="font-ibm font-semibold text-sm text-navy">{dept}</h3>
                              <span className="text-[10px] text-slate-400 font-ibm bg-slate-50 px-1.5 py-0.5 rounded">{deptSensors.length} sensorer</span>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 sm:h-7 text-xs gap-1"
                              onClick={() => addSensorToDept(dept)}
                              disabled={totalSensors >= maxSensors}
                              data-testid={`add-sensor-${dept}`}
                            >
                              <Plus className="h-3 w-3" /> Lägg till sensor
                            </Button>
                          </div>
                          <div className="space-y-3 sm:space-y-2">
                            {deptSensors.map((sensor, di) => (
                              <div key={sensor._idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <div className="flex items-center gap-2 flex-1">
                                  <span className="text-xs font-ibm text-slate-300 w-4 shrink-0 text-right">{di + 1}</span>
                                  <span className="text-xs font-ibm text-slate-400 shrink-0">{dept} –</span>
                                  <Input
                                    value={sensor.name}
                                    onChange={e => updateSensor(sensor._idx, 'name', e.target.value)}
                                    placeholder="T.ex. Toalett 101"
                                    className="flex-1 h-11 sm:h-9 text-base sm:text-sm"
                                    data-testid={`sensor-name-${sensor._idx}`}
                                  />
                                </div>
                                <div className="flex items-center gap-2 ml-6 sm:ml-0">
                                  <select
                                    value={sensor.type}
                                    onChange={e => updateSensor(sensor._idx, 'type', e.target.value)}
                                    className="rounded-md border border-slate-300 bg-white px-3 py-2.5 sm:py-1.5 text-sm sm:text-xs font-ibm flex-1 sm:flex-none sm:min-w-[100px] h-11 sm:h-9"
                                    data-testid={`sensor-type-${sensor._idx}`}
                                  >
                                    <option value="active">Aktiv sensor</option>
                                    <option value="shell">Tomt skal</option>
                                  </select>
                                  {deptSensors.length > 1 && (
                                    <Button variant="ghost" size="sm" className="h-11 w-11 sm:h-8 sm:w-8 p-0 text-slate-300 hover:text-red-400 shrink-0" onClick={() => removeSensor(sensor._idx)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  // No departments — flat list
                  <div className="space-y-2">
                    {form.sensors.map((sensor, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs font-ibm text-slate-400 w-6 shrink-0">{i + 1}.</span>
                        <Input
                          value={sensor.name}
                          onChange={e => updateSensor(i, 'name', e.target.value)}
                          placeholder="T.ex. Toalett vid matsal"
                          className="flex-1"
                          data-testid={`sensor-name-${i}`}
                        />
                        <select
                          value={sensor.type}
                          onChange={e => updateSensor(i, 'type', e.target.value)}
                          className="rounded-md border border-slate-300 bg-white px-2 py-2 text-xs font-ibm min-w-[100px]"
                          data-testid={`sensor-type-${i}`}
                        >
                          <option value="active">Aktiv</option>
                          <option value="shell">Skal</option>
                        </select>
                        {form.sensors.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-300 hover:text-red-400" onClick={() => removeSensor(i)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                    {totalSensors < maxSensors && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addSensorToDept('')} data-testid="add-sensor-flat">
                        <Plus className="h-3.5 w-3.5" /> Lägg till sensorplats
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Users */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="font-chivo font-bold text-lg text-navy">4. Personer med tillgång</h2>
                <p className="text-sm text-slate-500 font-ibm">Ange alla som ska ha tillgång till Vapelarm Cloud och om de ska få larmnotifikationer.</p>
                {form.users.map((user, i) => (
                  <div key={i} className="p-3 sm:p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-ibm font-semibold text-sm text-navy">Person {i + 1}</span>
                      {form.users.length > 1 && (
                        <Button variant="ghost" size="sm" className="h-9 w-9 sm:h-7 sm:w-7 p-0 text-red-400" onClick={() => removeUser(i)}><Trash2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" /></Button>
                      )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <Label className="font-ibm text-xs mb-1.5 block">Namn</Label>
                        <Input value={user.name} onChange={e => updateUser(i, 'name', e.target.value)} placeholder="Anna Andersson" className="h-11 sm:h-10 text-base sm:text-sm" data-testid={`user-name-${i}`} />
                      </div>
                      <div>
                        <Label className="font-ibm text-xs mb-1.5 block">E-post</Label>
                        <Input type="email" value={user.email} onChange={e => updateUser(i, 'email', e.target.value)} placeholder="anna@skola.se" className="h-11 sm:h-10 text-base sm:text-sm" data-testid={`user-email-${i}`} />
                      </div>
                      <div>
                        <Label className="font-ibm text-xs mb-1.5 block">Roll/funktion</Label>
                        <Input value={user.role} onChange={e => updateUser(i, 'role', e.target.value)} placeholder="T.ex. Rektor" className="h-11 sm:h-10 text-base sm:text-sm" data-testid={`user-role-${i}`} />
                      </div>
                    </div>
                    <label className="flex items-center gap-2.5 text-sm font-ibm cursor-pointer py-1">
                      <input type="checkbox" checked={user.receives_alarms} onChange={e => updateUser(i, 'receives_alarms', e.target.checked)} className="rounded h-5 w-5 sm:h-4 sm:w-4" />
                      Ska få larmnotifikationer
                    </label>
                    {user.receives_alarms && deptNames.length > 1 && (
                      <div>
                        <Label className="font-ibm text-xs">Larm från (lämna tomt = alla)</Label>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {deptNames.map(d => (
                            <button key={d} onClick={() => {
                              const deps = user.alarm_departments || [];
                              updateUser(i, 'alarm_departments', deps.includes(d) ? deps.filter(x => x !== d) : [...deps, d]);
                            }} className={`px-3 py-2 sm:px-2 sm:py-1 rounded text-sm sm:text-xs font-ibm border transition-colors ${(user.alarm_departments || []).includes(d) ? 'bg-navy text-white border-navy' : 'bg-white text-slate-600 border-slate-300'}`}>
                              {d}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addUser} className="gap-1.5 h-10 sm:h-9 px-4" data-testid="add-user-btn">
                  <Plus className="h-3.5 w-3.5" /> Lägg till person
                </Button>
              </div>
            )}

            {/* Step 5: Contact */}
            {step === 4 && (
              <div className="space-y-4">
                <h2 className="font-chivo font-bold text-lg text-navy">5. Huvudkontakt / administratör</h2>
                <p className="text-sm text-slate-500 font-ibm">En person på skolan som blir er huvudsakliga kontakt för systemet.</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="font-ibm text-xs">Namn</Label>
                    <Input value={form.primary_contact.name} onChange={e => updateContact('name', e.target.value)} placeholder="Anna Andersson" className="mt-1" data-testid="contact-name" />
                  </div>
                  <div>
                    <Label className="font-ibm text-xs">E-post</Label>
                    <Input type="email" value={form.primary_contact.email} onChange={e => updateContact('email', e.target.value)} placeholder="anna@skola.se" className="mt-1" data-testid="contact-email" />
                  </div>
                  <div>
                    <Label className="font-ibm text-xs">Telefon</Label>
                    <Input value={form.primary_contact.phone} onChange={e => updateContact('phone', e.target.value)} placeholder="070-123 45 67" className="mt-1" data-testid="contact-phone" />
                  </div>
                </div>
                <div>
                  <Label className="font-ibm text-xs">Övriga anteckningar (valfritt)</Label>
                  <Textarea value={form.notes} onChange={e => updateField('notes', e.target.value)} placeholder="T.ex. montage vecka 44, elektriker kontaktad" className="mt-1" rows={3} data-testid="notes" />
                </div>
              </div>
            )}

            {/* Step 6: Review */}
            {step === 5 && (
              <div className="space-y-5">
                <h2 className="font-chivo font-bold text-lg text-navy">Granska uppgifterna</h2>
                <p className="text-sm text-slate-500 font-ibm">Kontrollera att allt stämmer innan du skickar in.</p>

                <div className="space-y-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Skola</p>
                    <p className="font-ibm text-sm text-navy font-semibold">{form.school_name || '–'}</p>
                    <p className="font-ibm text-xs text-slate-500">{form.school_address || '–'}</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Avdelningar ({form.departments.filter(d => d.name).length})</p>
                    <p className="font-ibm text-sm text-navy">{form.departments.filter(d => d.name).map(d => d.name).join(', ') || '–'}</p>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Sensorer ({form.sensors.filter(s => s.name).length} av {maxSensors})</p>
                    <div className="mt-1 space-y-0.5">
                      {form.sensors.filter(s => s.name).map((s, i) => {
                        const fullName = s.department && s.name ? `${s.department} – ${s.name}` : s.name;
                        return (
                          <p key={i} className="font-ibm text-xs text-slate-600">
                            {fullName} — <span className={s.type === 'active' ? 'text-emerald-600 font-semibold' : 'text-slate-400'}>{s.type === 'active' ? 'Aktiv' : 'Skal'}</span>
                          </p>
                        );
                      })}
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Användare ({form.users.filter(u => u.name).length})</p>
                    <div className="mt-1 space-y-0.5">
                      {form.users.filter(u => u.name).map((u, i) => (
                        <p key={i} className="font-ibm text-xs text-slate-600">
                          {u.name} — {u.email} — {u.role} {u.receives_alarms && <span className="text-blue-600">(larm)</span>}
                        </p>
                      ))}
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Huvudkontakt</p>
                    <p className="font-ibm text-sm text-navy font-semibold">{form.primary_contact.name || '–'}</p>
                    <p className="font-ibm text-xs text-slate-500">{form.primary_contact.email} · {form.primary_contact.phone}</p>
                  </div>

                  {form.notes && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-ibm font-semibold text-xs text-slate-400 uppercase">Anteckningar</p>
                      <p className="font-ibm text-sm text-slate-600">{form.notes}</p>
                    </div>
                  )}
                </div>

                {error && <p className="text-sm text-red-600 font-ibm">{error}</p>}
              </div>
            )}

          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between gap-3 mt-6">
          <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="gap-1.5 h-12 sm:h-10 flex-1 sm:flex-none text-base sm:text-sm" data-testid="prev-step-btn">
            <ChevronLeft className="h-4 w-4" /> Föregående
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(s => s + 1)} className="gap-1.5 h-12 sm:h-10 flex-1 sm:flex-none text-base sm:text-sm" data-testid="next-step-btn">
              Nästa <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="gap-1.5 h-12 sm:h-10 flex-1 sm:flex-none text-base sm:text-sm bg-emerald-600 hover:bg-emerald-700" data-testid="submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Skicka in
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
